import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { remCalc, layout, media } from '@prisjakt/ui-base';
import LogoComponent from '@prisjakt/ui-logo';
import { NewsletterSignupFooter } from 'src/components/newsletter-signup';
import { OutlinedButton } from '@prisjakt/ui/button/variants';
import { H4Text } from '@prisjakt/ui/text/variants';

const pageWithSidebarContent =
  layout.pageContentMaxWidthVal + layout.sidebarMenuWidth.desktop.value * 2;

interface FooterProps {
  sidebarOpen?: boolean;
}

export const Footer = styled.footer<FooterProps>`
  transition: all 200ms ease;

  /* Offset footer to the left behind the invisible sidebar wrapper
   to keep the footer full width */
  @media screen and (min-width: ${pageWithSidebarContent}px) {
    margin-left: ${props =>
      props.sidebarOpen
        ? 0
        : `calc(((1250px - 100vw) / 2) + ${layout.sidebarMenuWidth.desktop.rem})`};
  }

  @media screen and (min-width: 1980px) {
    margin-left: ${props =>
      props.sidebarOpen
        ? 0
        : `calc(-1 * ${layout.sidebarContentWidth.desktop.rem})`};
  }
`;

export const Content = styled.div<FooterProps>`
  max-width: ${layout.pageContent.maxWidth.rem};
  display: flex;
  width: 100%;
  transition: all 200ms ease;
  padding: ${layout.measurements.spacing150} ${layout.measurements.spacing100};

  /* Align footer content with the rest of the page */
  @media screen and (min-width: ${pageWithSidebarContent}px) {
    margin-left: ${props =>
      props.sidebarOpen
        ? 'inherit'
        : `calc(((100vw - 1250px) / 2) - ${layout.sidebarMenuWidth.desktop.rem})`};
  }

  @media screen and (min-width: 1980px) {
    margin-left: ${props =>
      props.sidebarOpen
        ? `calc(((100vw - 1250px) / 2) - ${layout.sidebarMenuWidth.desktop
            .value + layout.sidebarContentWidth.desktop.value}px)`
        : `calc(((100vw - 1250px) / 2) - ${layout.sidebarMenuWidth.desktop.rem})`};
  }
`;

export const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  gap: ${layout.measurements.spacing150};
  align-items: flex-start;

  ${media.mediumDown`
    grid-template-columns: 1fr;
  `}
`;

export const Download = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.mediumUp`
    min-width: 180px;
  `};

  ${media.mediumDown`
    order: -1;
    flex-basis: 100%;
  `}
`;

export const DownloadTitle = styled(H4Text)`
  margin-bottom: ${layout.measurements.spacing100};
`;

export const DownloadIcons = styled.div`
  ${media.mediumUp`
    > :not(:last-child) {
      margin-bottom: ${layout.measurements.spacing050};
    }
  `};

  ${media.mediumDown`
    display: flex;
    flex-wrap: nowrap;
    > :not(:last-child) {
      margin-right: ${layout.measurements.spacing050};
    }
  `};
`;

export const SocialIcons = styled.div`
  display: flex;
  align-items: center;
`;

export const SocialIcon = styled.a`
  color: ${p => p.theme.colors.base500};
  margin: 0 ${remCalc(4)};
  line-height: 0;
`;

export const Sites = styled.div`
  width: 232px;
`;

export const CoBranding = styled.div`
  align-items: flex-end;
  text-align: left;
  gap: ${layout.measurements.spacing100};

  ${media.mediumDown`
    min-height: 90px;
    && {
      align-items: center;
      flex-direction: column;
    }
  `}
`;

export const CoBrandingLink = styled(RouterLink)`
  color: ${p => p.theme.colors.base500};
  text-decoration: underline;
`;

export const TopSection = styled.div`
  background: ${p => p.theme.colors.maybeBlue100};

  ${media.mediumDown`
    display: flex;
    justify-content: center;

    ${Content} {
      max-width: 370px;
      flex-direction: column;
      text-align: center;
      gap: ${layout.measurements.spacing150};
    }
  `};
`;

export const MidSection = styled.div`
  justify-content: space-between;
  background: ${p => p.theme.footer.bg.primary};

  ${Content} {
    padding-top: ${layout.measurements.spacing250};

    ${media.mediumDown`
      padding-top: ${layout.measurements.spacing150};
    `}
  }
`;

export const BottomSection = styled.div`
  background: ${p => p.theme.footer.bg.primary};
  color: ${p => p.theme.colors.base500};

  ${Content} {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-top: ${layout.measurements.spacing150};
    flex-wrap: wrap;
    gap: ${layout.measurements.spacing100} ${layout.measurements.spacing150};

    /* CoBranding */
    :last-child {
      border-top: 1px solid ${p => p.theme.colors.base200};
      align-items: center;
      text-align: center;
    }

    ${media.mediumDown`
      flex-direction: column;
      gap: ${layout.measurements.spacing100} 0;
    `};
  }
`;

export const Logo = styled(LogoComponent)`
  width: 180px;
  max-width: 100%;
`;

export const LogoWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
  gap: ${layout.measurements.spacing050};

  p {
    margin-bottom: 6px;
  }

  ${media.mediumDown`
    flex-direction: column;
    align-items: center;
    p {
      margin-bottom: 0;
    }
  `};
`;

export const NewsletterSignup = styled(NewsletterSignupFooter)`
  width: 100%;

  input {
    display: flex;
    flex-grow: 1;
  }
`;

export const NewsLetterIllustration = styled.img`
  height: 100px;
  width: 189px;
  margin: 0 ${layout.measurements.spacing250};

  ${media.mediumDown`
    margin: 0 auto;
  `}
`;

export const FeedbackButton = styled(OutlinedButton)`
  margin-bottom: ${layout.measurements.spacing150};

  ${media.mediumDown`
    width: min-content;
  `};

  @media screen and (max-width: 430px) {
    width: 100%;
  }
`;

export const ButtonText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ClassicSwitchWrapper = styled.div`
  ${media.largeUp`
    width: 100%;
    order: 99;
  `};
`;

export const ClassicSwitchLink = styled.span`
  color: ${p => p.theme.colors.base500};
  text-decoration: underline;

  span {
    color: inherit;
  }
`;
